// src/pages/projects/boqUploadService.js

import {
  collection, doc, getDocs, addDoc, setDoc,
  query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { reserveItemId } from "../../services/itemIdService";

// ── FETCH ALL ACTIVE ITEMS FOR COMPANY ────────────────────────────────────────
export const fetchActiveItems = async (company_id) => {
  const snap = await getDocs(
    query(
      collection(db, "items"),
      where("company_id", "==", company_id),
      where("is_active",  "==", true)
    )
  );
  return snap.docs.map(d => ({ docId: d.id, ...d.data() }));
};

// Normalizes a name into a word-sorted, punctuation-stripped key — so
// "Copper Piping" and "Piping Copper" (or "Copper-Piping") land on the
// SAME key, catching word-order/punctuation duplicates that exact-string
// matching (the "name:" key below) misses. Still fully deterministic (no
// AI involved), per Section 24's Deterministic-Matching-Over-AI rule.
export const normalizeItemKey = (name = "") =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(" ");

// ── BUILD 4-KEY O(1) LOOKUP MAP (+ normalized fallback key) ───────────────────
export const buildItemsMaster = (items) => {
  const map = {};
  items.forEach(item => {
    const byId    = (item.item_id   || "").trim();
    const byDocId = (item.docId     || "").trim();
    const byCode  = (item.item_code || "").trim();
    const byName  = `name:${(item.item_name || "").toLowerCase().trim()}`;
    const byNorm  = `norm:${normalizeItemKey(item.item_name || "")}`;
    if (byId)    map[byId]    = item;
    if (byDocId) map[byDocId] = item;
    if (byCode)  map[byCode]  = item;
    if (byName)  map[byName]  = item;
    // Only set if no exact-name match already claimed this key — a true
    // exact match must never be overridden by a looser normalized one.
    if (byNorm && !map[byNorm]) map[byNorm] = item;
  });
  return map;
};

// ── GENERATE NEXT ITEM-XXXXX ID ───────────────────────────────────────────────
// D-300: platform-wide id from the shared counter (itemIdService.js).
// The old per-company "max + 1" gave every company its own ITEM-00001.
// Call this ONCE PER NEW ITEM — never add 1 to its result yourself.
export const generateNextItemId = async () => reserveItemId();

// ── GENERATE ITEM CODE FROM NAME ──────────────────────────────────────────────
const makeItemCode = (item_id, name) => {
  const shortCode = name.trim()
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, "")
    .split(" ").filter(Boolean).slice(0, 3)
    .map(w => w.substring(0, 4)).join("-");
  return shortCode ? `MT-${shortCode}` : item_id;
};

// ── CREATE NEW ITEM + INVENTORY SUMMARY ───────────────────────────────────────
// Returns the created item object with item_id and item_code.
// NOTE: item_id is generated fresh per call; caller must pass unique item_ids
// when creating multiple items in one session (use generateNextItemId serially).
// specification (optional, 14 Sep 2026): keeps item_name short/scannable
// for dropdowns while the full technical detail (thickness, insulation,
// construction, standards) lives here instead — mirrors the split already
// applied in ItemMaster.js's manual "Add New Item" form and parseBoqWithAI.js's
// item_name/description extraction fields, so a new item created via
// AI BOQ Import or manual BOQ upload gets the same clean short-name
// treatment as one added by hand. Optional and backward-compatible —
// omitting it (existing callers) just leaves the field blank, same as
// any pre-14-Sep item.
export const createNewItem = async (company_id, item_id, name, unit, specification = "") => {
  const item_code = makeItemCode(item_id, name);

  const newItemRef = doc(collection(db, "items"));
  await setDoc(newItemRef, {
    item_id,
    item_code,
    item_name:       name.trim(),
    item_name_lower: name.toLowerCase().trim(),
    specification:   (specification || "").trim(),
    unit,
    category:        "MATERIAL",
    company_id,
    is_active:       true,
    minimum_stock:   0,
    created_at:      serverTimestamp(),
    source:          "boq_upload",
  });

  await setDoc(doc(db, "inventory_summary", item_id), {
    item_id,
    item_code,
    item_name:       name.trim(),
    specification:   (specification || "").trim(),
    unit,
    category:        "MATERIAL",
    company_id,
    qty:             0,
    defective_stock: 0,
    scrap_stock:     0,
    is_active:       true,
    created_at:      serverTimestamp(),
  });

  return { item_id, item_code, item_name: name.trim(), specification: (specification || "").trim(), unit };
};

// ── FETCH EXISTING STAGES FOR PROJECT ─────────────────────────────────────────
export const fetchExistingStageNames = async (company_id, project_id) => {
  const snap = await getDocs(
    query(
      collection(db, "project_material_stages"),
      where("company_id", "==", company_id),
      where("project_id", "==", project_id)
    )
  );
  const map = {};
  snap.docs.forEach(d => {
    const data = d.data();
    map[(data.stage_name || "").toLowerCase().trim()] = { id: d.id, ...data };
  });
  return map; // key: stage_name.toLowerCase() → stage doc
};

// ── GET NEXT STAGE NO FOR PROJECT ─────────────────────────────────────────────
const getNextStageNo = async (company_id, project_id) => {
  const snap = await getDocs(
    query(
      collection(db, "project_material_stages"),
      where("company_id", "==", company_id),
      where("project_id", "==", project_id)
    )
  );
  return snap.size + 1;
};

// ── BUILD BUDGET ITEM FROM PARSED ROW + RESOLVED ITEM ─────────────────────────
const buildBudgetItem = (row, resolvedItem) => {
  const side       = row.side_normalized; // "high_side" | "low_side"
  const buffer_pct = side === "low_side" ? 15 : 0;
  const qty_raw    = Number(row.qty || 0);
  const qty_final  = side === "low_side"
    ? Math.ceil(qty_raw * (1 + buffer_pct / 100))
    : qty_raw;
  const cost_rate  = Number(row.rate || 0);

  return {
    item_id:       resolvedItem.item_id   || "",
    item_code:     resolvedItem.item_code || "",
    item_name:     resolvedItem.item_name || row.item_name,
    unit:          resolvedItem.unit      || row.unit || "Nos",
    side,
    buffer_pct,
    qty_entered:   qty_raw,
    qty_budgeted:  qty_final,
    qty_issued:    0,
    qty_remaining: qty_final,
    cost_rate,
    budget_amount: qty_final * cost_rate,
    issued_amount: 0,
    alert_level:   "ok",
    budget_pct:    0,
    override_log:  [],
  };
};

// ── SAVE BOQ STAGES ───────────────────────────────────────────────────────────
// stages: array of { stage_name, rows: [{...parsed row with resolvedItem attached}] }
// replaceExisting: Set of stage_name (lowercase) to replace instead of merge
export const saveBOQStages = async ({
  company_id,
  project_id,
  project_name,
  stages,
  replaceExisting,
  existingStageMap,
  user,
}) => {
  let stagesCreated = 0;
  let itemsImported = 0;

  for (const stage of stages) {
    const nameKey        = (stage.stage_name || "").toLowerCase().trim();
    const existingStage  = existingStageMap[nameKey];
    const isReplace      = replaceExisting.has(nameKey);

    const budgetItems = stage.rows.map(row => buildBudgetItem(row, row.resolvedItem));
    itemsImported += budgetItems.length;

    if (existingStage && !isReplace) {
      // Merge — append items that don't already exist by name
      const existingNames = new Set(
        (existingStage.material_budget || []).map(i => i.item_name.toLowerCase().trim())
      );
      const newItems = budgetItems.filter(
        i => !existingNames.has(i.item_name.toLowerCase().trim())
      );
      if (newItems.length > 0) {
        const { updateDoc, doc: firestoreDoc } = await import("firebase/firestore");
        await updateDoc(firestoreDoc(db, "project_material_stages", existingStage.id), {
          material_budget: [...(existingStage.material_budget || []), ...newItems],
          updated_at:      serverTimestamp(),
        });
      }
    } else if (existingStage && isReplace) {
      // Replace — overwrite material_budget
      const { updateDoc, doc: firestoreDoc } = await import("firebase/firestore");
      await updateDoc(firestoreDoc(db, "project_material_stages", existingStage.id), {
        material_budget: budgetItems,
        updated_at:      serverTimestamp(),
      });
    } else {
      // Create new stage
      const stage_no = await getNextStageNo(company_id, project_id);
      await addDoc(collection(db, "project_material_stages"), {
        company_id,
        project_id,
        project_name,
        stage_no,
        stage_name:      stage.stage_name,
        trigger_items:   [],
        status:          "locked",
        material_budget: budgetItems,
        created_by:      user.uid,
        created_by_name: user.name || user.email || "",
        created_at:      serverTimestamp(),
        updated_at:      serverTimestamp(),
      });
      stagesCreated++;
    }
  }

  return { stagesCreated, itemsImported };
};
